import React, { useState } from 'react';
import { Download, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { api, getToken, type Profile } from '../lib/api';
import { Field, INPUT, LegalLink } from './ProfileFields';
import { BRAND } from '../brand';

/**
 * Los derechos sobre los propios datos: descargarlos y borrarlos.
 *
 * Las leyes de datos de casi todos los países (RGPD, LGPD, CCPA, Ley 172-13…)
 * dan ambos derechos, y el modo honesto de cumplirlos es un botón, no un
 * correo que alguien tiene que acordarse de contestar.
 */
export const AccountDataPanel: React.FC<{
  profile: Profile;
  /** La cuenta ya no existe: hay que cerrar la sesión en el navegador. */
  onDeleted: () => void;
}> = ({ profile, onDeleted }) => {
  const [descargando, setDescargando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [secreto, setSecreto] = useState('');
  const [borrando, setBorrando] = useState(false);
  const [error, setError] = useState('');

  const descargar = async () => {
    setError('');
    setDescargando(true);
    try {
      // Con fetch y no con un enlace: la sesión va en una cabecera, no en
      // una cookie, y un <a href> no la llevaría.
      const res = await fetch('/api/account/export', {
        headers: { Authorization: `Bearer ${getToken() ?? ''}` },
      });
      if (!res.ok) throw new Error('No se pudieron preparar tus datos. Prueba en un rato.');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `open-dahu-${profile.username}.json`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo descargar.');
    } finally {
      setDescargando(false);
    }
  };

  const borrar = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBorrando(true);
    try {
      await api.delete<{ ok: true }>('/account', profile.hasPassword ? { password: secreto } : { username: secreto });
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo borrar la cuenta.');
      setBorrando(false);
    }
  };

  return (
    <div className="px-6 py-5 space-y-5">
      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-ink-100">Descargar tus datos</h3>
        <p className="text-[0.8125rem] leading-relaxed text-ink-400">
          Un archivo con todo lo que guardamos de tu cuenta: datos personales, partidas,{' '}
          {BRAND.currencyName}, trivias, favoritas y logros. Lo que no incluye
          son tu contraseña ni tu clave de IA, que no se pueden leer de vuelta. Más detalle en la{' '}
          <LegalLink to="privacidad">Política de privacidad</LegalLink>.
        </p>
        <button
          type="button"
          onClick={() => void descargar()}
          disabled={descargando}
          className="flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-semibold bg-ink-850 hover:bg-ink-800
                     border border-ink-700 text-ink-100 disabled:text-ink-500 transition-colors"
        >
          {descargando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Descargar mis datos
        </button>
      </section>

      <section className="space-y-2 pt-4 border-t border-ink-800">
        <h3 className="text-sm font-semibold text-danger-400">Borrar tu cuenta</h3>
        <p className="text-[0.8125rem] leading-relaxed text-ink-400">
          Se borran para siempre tu cuenta, tus estadísticas, tus {BRAND.currencyName}, tu historial y{' '}
          <strong className="text-ink-200">todas las trivias que hayas creado</strong>. No se puede
          deshacer.
        </p>

        {!confirmando ? (
          <button
            type="button"
            onClick={() => setConfirmando(true)}
            className="flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-semibold
                       border border-danger-400/40 text-danger-400 hover:bg-danger-400/10 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Borrar mi cuenta
          </button>
        ) : (
          <form onSubmit={borrar} className="space-y-3">
            <Field
              label={profile.hasPassword ? 'Tu contraseña, para confirmar' : `Escribe «${profile.username}» para confirmar`}
            >
              <input
                type={profile.hasPassword ? 'password' : 'text'}
                value={secreto}
                onChange={(e) => setSecreto(e.target.value)}
                autoComplete={profile.hasPassword ? 'current-password' : 'off'}
                autoFocus
                required
                className={INPUT}
              />
            </Field>
            <div className="flex items-center gap-2">
              <button
                type="submit"
                disabled={!secreto || borrando}
                className="flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-semibold
                           bg-danger-500 hover:bg-danger-400 text-white
                           disabled:bg-ink-800 disabled:text-ink-500 transition-colors"
              >
                {borrando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Borrar para siempre
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmando(false);
                  setSecreto('');
                }}
                className="px-4 h-10 rounded-lg text-sm text-ink-400 hover:text-ink-100"
              >
                Cancelar
              </button>
            </div>
          </form>
        )}
      </section>

      {error && (
        <p
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-danger-400/30 bg-danger-400/10 px-3 py-2.5 text-danger-400 text-[0.8125rem]"
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
          {error}
        </p>
      )}
    </div>
  );
};
